import { useCallback, useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button';
import { ReloadIcon } from '@radix-ui/react-icons';

import { useAppDispatch } from 'src/app/store/hooks';
import {
  PassengerRead,
  usePassengersUndeleteCreateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { Modal } from 'src/common/primitives/Modal';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

export const PassengerRestore = ({
  passenger,
}: {
  passenger: PassengerRead;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="pr-6">
      <ReloadIcon
        className="h-5 w-5 cursor-pointer hover:text-mint"
        onClick={() => setOpen(true)}
        data-testid="restore-passenger-button"
      />
      <RestorePassengerModal
        passenger={passenger}
        open={open}
        setOpen={setOpen}
      />
    </div>
  );
};

interface RestorePassengerModalProps {
  passenger: PassengerRead;
  open: boolean;
  setOpen: (open: boolean) => void;
}

function RestorePassengerModal({
  passenger,
  open,
  setOpen,
}: RestorePassengerModalProps) {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [triggerRestore] = usePassengersUndeleteCreateMutation({});

  const onRestore = useCallback(async () => {
    setLoading(true);
    try {
      await triggerRestore({
        id: passenger.id,
        passenger: passenger,
      }).unwrap();
      dispatch(
        show({
          type: 'success',
          title: 'Passenger restored',
        })
      );
      setOpen(false);
    } catch (error) {
      dispatch(
        show({
          type: 'error',
          title: 'Unable to restore passenger',
          description: formatServerError(error),
        })
      );
    } finally {
      setLoading(false);
    }
  }, [triggerRestore, passenger, dispatch, setOpen]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      contentClassnames="max-w-md"
      title="Restore Passenger"
    >
      <div className="flex flex-col items-center gap-2">
        <div className="mb-3 flex h-[48px] w-[48px] items-center justify-center rounded-full bg-mint text-white">
          <ReloadIcon className="h-5 w-5 text-white" />
        </div>
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Restore Passenger
        </h3>
        <p className="mt-1 text-center text-sm text-gray-500">
          Click the button below to restore passenger {passenger.full_name}.
        </p>
      </div>
      <div className="mt-6 flex items-center justify-evenly gap-3">
        <Button
          className="w-full"
          onClick={() => setOpen(false)}
          disabled={loading}
          dataTestId="cancel-restore-passenger-button"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          className="w-full"
          onClick={onRestore}
          disabled={loading}
          dataTestId="confirm-restore-passenger-button"
        >
          {loading ? 'Restoring' : 'Restore'} Passenger
        </Button>
      </div>
    </Modal>
  );
}
